<template>
  <div class="mobile-card-list">
    <!-- 数据信息 -->
    <div class="mobile-card-list-info">
      <span class="info-text">
        从 <span class="info-number">{{ formatNumber(dbTotalRows) }}</span> 条记录中检索到 <span class="info-number">{{ total.toLocaleString() }}</span> 条数据
      </span>
    </div>

    <!-- 卡片列表 -->
    <div
      ref="scrollContainer"
      class="mobile-card-list-container"
      @scroll="handleScroll"
    >
      <div v-if="loading && displayData.length === 0" class="mobile-card-list-loading">
        <a-spin size="large" />
      </div>
      <template v-else>
        <div
          v-for="(record, index) in displayData"
          :key="getRecordKey(record, index)"
          class="mobile-card-item"
          @click="handleCardClick(record)"
        >
          <div class="mobile-card-content">
            <!-- 遍历所有可见列，按照列配置顺序显示 -->
            <div
              v-for="column in visibleColumns"
              :key="column.key"
              class="mobile-card-field"
            >
              <div class="mobile-card-label-wrapper">
                <span class="mobile-card-label">{{ column.label || column.title }}：</span>
              </div>
              <div class="mobile-card-value-wrapper">
                <div class="mobile-card-value">
                <!-- 标签类型 -->
                <template v-if="shouldRenderAsTag(column.key)">
                  <div v-if="record[column.key]" class="mobile-card-tags">
                    <a-tag
                      v-for="(tag, tagIndex) in getTagList(record[column.key])"
                      :key="tagIndex"
                      class="mobile-card-tag"
                    >
                      {{ tag }}
                    </a-tag>
                  </div>
                  <span v-else class="mobile-card-empty">-</span>
                </template>
                <!-- 列表类型 -->
                <template v-else-if="isListType(column.key)">
                  <template v-if="getListItems(record[column.key]).length > 0">
                    <span v-for="(item, itemIndex) in getListItems(record[column.key])" :key="itemIndex">
                      {{ item }}<span v-if="itemIndex < getListItems(record[column.key]).length - 1">, </span>
                    </span>
                  </template>
                  <span v-else class="mobile-card-empty">-</span>
                </template>
                <!-- 链接类型 -->
                <template v-else-if="isLinkType(column.key)">
                  <a
                    v-if="record[column.key]"
                    :href="record[column.key]"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mobile-card-link"
                    @click.stop
                  >
                    {{ record[column.key] }}
                  </a>
                  <span v-else class="mobile-card-empty">-</span>
                </template>
                <!-- 描述类型（长文本） -->
                <template v-else-if="isDescriptionType(column.key)">
                  <div class="mobile-card-description">{{ record[column.key] || '-' }}</div>
                </template>
                <!-- 普通文本 -->
                <template v-else>
                  {{ getCellDisplayValue(column.key, record) || '-' }}
                </template>
                </div>
              </div>
            </div>
          </div>
          <!-- 操作按钮 -->
          <div class="mobile-card-actions" @click.stop>
            <slot name="actions" :record="record">
              <a-button type="primary" size="small" class="mobile-card-action-btn" @click="handleViewDetail(record)">
                查看详情
              </a-button>
            </slot>
          </div>
        </div>

        <!-- 加载更多指示器 -->
        <div v-if="loading && displayData.length > 0" class="mobile-card-list-loading-more">
          <a-spin size="small" />
          <span class="loading-text">加载中...</span>
        </div>

        <!-- 没有更多数据 -->
        <div v-if="!hasMore && displayData.length > 0" class="mobile-card-list-no-more">
          没有更多数据了
        </div>

        <!-- 空状态 -->
        <div v-if="!loading && displayData.length === 0" class="mobile-card-list-empty">
          <div class="empty-text">暂无数据</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { formatCreateTime } from '@/utils/formatCreateTime'

export default {
  name: 'MobileCardList',
  props: {
    columns: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    dbTotalRows: {
      type: Number,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: 10
    }
  },
  emits: ['row-click', 'load-more', 'view-detail'],
  data() {
    return {
      displayData: [],
      currentPage: 1,
      isLoadingMore: false,
      scrollThreshold: 100, // 距离底部多少像素时触发加载
      lastScrollTop: 0 // 记录上次滚动位置，防止重复触发
    }
  },
  computed: {
    visibleColumns() {
      // 将列配置转换为统一格式，确保有 label 和 title
      return this.columns
        .filter(col => col.visible !== false)
        .map(col => ({
          ...col,
          label: col.label || col.title || col.key,
          title: col.title || col.label || col.key
        }))
    },
    hasMore() {
      // 检查是否还有更多数据：当前显示的数据量小于总数
      const hasMore = this.displayData.length < this.total
      return hasMore && this.total > 0
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(newData) {
        // 直接使用传入的 data，因为父组件已经处理了追加逻辑
        this.displayData = newData ? [...newData] : []
        // 数据更新后，如果不在加载中，重置加载状态
        if (!this.loading) {
          this.$nextTick(() => {
            this.isLoadingMore = false
          })
        }
      }
    },
    loading(newVal) {
      // loading 结束后，重置加载状态
      if (!newVal) {
        this.$nextTick(() => {
          this.isLoadingMore = false
        })
      }
    }
  },
  methods: {
    handleScroll(event) {
      const container = event.target
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      // 防止向上滚动时触发
      if (scrollTop < this.lastScrollTop) {
        this.lastScrollTop = scrollTop
        return
      }
      this.lastScrollTop = scrollTop

      // 检查是否接近底部
      const distanceToBottom = scrollHeight - scrollTop - clientHeight
      if (distanceToBottom <= this.scrollThreshold && distanceToBottom >= 0) {
        this.loadMore()
      }
    },
    loadMore() {
      // 防止重复触发
      if (this.isLoadingMore || !this.hasMore || this.loading) {
        return
      }

      this.isLoadingMore = true
      const nextPage = this.currentPage + 1
      this.currentPage = nextPage
      this.$emit('load-more', nextPage)
    },
    reset() {
      this.displayData = []
      this.currentPage = 1
      this.isLoadingMore = false
      this.lastScrollTop = 0
    },
    handleCardClick(record) {
      this.$emit('row-click', record)
    },
    handleViewDetail(record) {
      this.$emit('view-detail', record)
    },
    getRecordKey(record, index) {
      return record.id || record.source_unique_id || `card-${index}`
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '-'
      return num.toLocaleString()
    },
    formatCellText(value) {
      if (value === null || value === undefined || value === '') return ''
      if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean).join('、')
      }
      return String(value)
    },
    getCellDisplayValue(columnKey, record) {
      if (columnKey === 'create_time' || columnKey === 'update_time') {
        return formatCreateTime(record?.[columnKey])
      }
      return this.formatCellText(record?.[columnKey])
    },
    shouldRenderAsTag(key) {
      // 只有前沿技术的技术领域标签和疾病领域标签用 tag 处理（与 DataTable 保持一致）
      const tagFields = ['tech_areas_tag', 'disease_states_tag']
      return tagFields.includes(key)
    },
    getTagList(value) {
      if (!value) return []
      if (Array.isArray(value)) {
        return value
      }
      if (typeof value === 'string') {
        return value.split(/[,，]/).map(tag => tag.trim()).filter(tag => tag)
      }
      return []
    },
    isListType(key) {
      const column = this.columns.find(col => col.key === key)
      return column && column.type === 'list'
    },
    getListItems(value) {
      if (!value) return []
      if (Array.isArray(value)) {
        return value
      }
      if (typeof value === 'string') {
        return value.split(/[,，]/).map(item => item.trim()).filter(item => item)
      }
      return []
    },
    isLinkType(key) {
      return key === 'resource_link' || key === 'source_link' || key === 'link'
    },
    isDescriptionType(key) {
      return key === 'description' || key === 'summary'
    }
  },
  beforeUnmount() {
    // 组件销毁时重置状态
    this.reset()
  }
}
</script>

<style scoped>
.mobile-card-list {
  width: 100%;
  background: #f5f5f5;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  height: 0;
}

.mobile-card-list-info {
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #ebedf0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  flex-shrink: 0;
}

.info-number {
  color: #1989fa;
  font-weight: 500;
}

.mobile-card-list-container {
  padding: 12px;
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}

.mobile-card-item {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
}

.mobile-card-item:active {
  background: #f7f8fa;
  transform: scale(0.98);
}

.mobile-card-content {
  margin-bottom: 12px;
}

.mobile-card-field {
  display: flex;
  margin-bottom: 12px;
  align-items: flex-start;
}

.mobile-card-field:last-child {
  margin-bottom: 0;
}

.mobile-card-label-wrapper {
  flex-shrink: 0;
  width: 110px;
  padding-right: 12px;
  padding-top: 0;
}

.mobile-card-label {
  display: block;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
}

.mobile-card-value-wrapper {
  flex: 1;
  min-width: 0;
  padding-top: 0;
}

.mobile-card-value {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  word-break: break-word;
  line-height: 1.5;
  text-align: left;
  margin: 0;
  padding: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-card-empty {
  color: rgba(0, 0, 0, 0.25);
}

.mobile-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
  max-height: 48px;
  overflow: hidden;
}

.mobile-card-tag {
  margin: 0;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.mobile-card-link {
  color: #1989fa;
  text-decoration: none;
  word-break: break-all;
}

.mobile-card-link:active {
  color: #096dd9;
}

.mobile-card-description {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  margin: 0;
  padding: 0;
}

.mobile-card-actions {
  padding-top: 12px;
  border-top: 1px solid #ebedf0;
  display: flex;
  justify-content: flex-end;
}

.mobile-card-action-btn {
  width: 100%;
  height: 36px;
  font-size: 14px;
  border-radius: 6px;
}

.mobile-card-list-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.mobile-card-list-loading-more {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  gap: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.loading-text {
  margin-left: 8px;
}

.mobile-card-list-no-more {
  text-align: center;
  padding: 16px 0;
  color: rgba(0, 0, 0, 0.25);
  font-size: 13px;
}

.mobile-card-list-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
}

.empty-text {
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}
</style>
